import React from 'react';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Container from 'react-bootstrap/Container';
import Jumbotron from 'react-bootstrap/Jumbotron';
import DurationIcon from '../../svgs/duration.svg';
import EnrollmentIcon from '../../svgs/enrollment.svg';
import ExternshipIcon from '../../svgs/externship.svg';
import FacebookIcon from '../../svgs/facebook.svg';
import TwitterIcon from '../../svgs/twitter.svg';
import BootcampSessionDatesIcon from '../../svgs/bootcampsessiondates.svg';
import { CourseScheduleModel, ImageModel, LinkModel, InfoSecModel } from '../../models/globals';
import { IconLink } from '../common/IconLink';
import { ReadMoreHtml, IReadMoreHtml } from '../common/ReadMoreHtml';
import { getDurationTextForBanner, getEnrollmentDateTextForBanner } from '../../utilities/ProductUtilities';
import { ProductClassification, ProductType } from '../../models/enums';
import { MediaPlayer } from '../common/MediaPlayer';
import { Picture } from '../html5/Picture';
import { InfoSecModal } from './InfoSecModal';

interface ProductBannerProps {
    accessTime: string | null;
    courseDuration: number; // 0 when null in db
    classification: ProductClassification;
    type: ProductType;
    description: IReadMoreHtml;
    videoUrl: string | null;
    facebookLink: LinkModel;
    infosecModel: InfoSecModel;
    image: ImageModel;
    defaultImage: ImageModel;
    name: string;
    twitterLink: LinkModel;
    courseSchedules: CourseScheduleModel[];
    isExternshipProgram: boolean;
}

const ProductBanner: React.FC<ProductBannerProps> = (banner) => {
    const [videoPlaybackError, setVideoPlaybackError] = React.useState(false);
    const [videoNotAvailable, setVideoNotAvailable] = React.useState(false);
    const {
        description,
        facebookLink,
        infosecModel,
        image,
        defaultImage,
        name,
        twitterLink,
        isExternshipProgram,
        videoUrl,
    } = banner;
    const [showInfosecModal, setShowInfosecModal] = React.useState(false);

    return (
        <>
            <Jumbotron className="bg-e2gdark text-white mb-0" fluid>
                <Container>
                    <Row>
                        <Col className="mb-3" md={5}>
                            {videoPlaybackError || videoNotAvailable || !videoUrl ? (
                                <Picture fluid defaultImage={defaultImage} {...image} />
                            ) : (
                                <MediaPlayer
                                    id="video-control"
                                    name={name}
                                    videoUrl={videoUrl}
                                    onVideoPlaybackError={() => setVideoPlaybackError(true)}
                                    onVideoNotAvailable={() => setVideoNotAvailable(true)}
                                />
                            )}
                        </Col>
                        <Col md={7}>
                            <h1
                                dangerouslySetInnerHTML={{
                                    __html: name,
                                }}
                            ></h1>
                            <ReadMoreHtml
                                btnClassName="font-weight-bold"
                                data-testid="banner-description"
                                ariaLabelSuffix="about this course"
                                {...description}
                            />
                            <Row className="justify-content-between">
                                <Col lg={{ order: 'last', span: 'auto' }}>
                                    <div className="py-2">
                                        <div className="small d-lg-inline-block py-2">SHARE</div>
                                        <IconLink Icon={FacebookIcon} {...facebookLink} />
                                        <IconLink Icon={TwitterIcon} {...twitterLink} />
                                    </div>
                                    <div className="border-bottom border-white d-lg-none" />
                                </Col>
                                <Col>
                                    <Row>
                                        <Col>
                                            <div className="d-lg-inline-block py-2 pr-2">
                                                <DurationIcon aria-label="duration icon" className="text-white" />
                                                <span className="pl-1">{getDurationTextForBanner(banner)}</span>
                                            </div>
                                            <div className="d-lg-inline-block py-2 pr-2">
                                                <EnrollmentIcon
                                                    aria-label="enrollment status icon"
                                                    className="text-white"
                                                />
                                                <span className="pl-1">{getEnrollmentDateTextForBanner(banner)}</span>
                                            </div>
                                            {infosecModel.sessionDates.length > 0 && (
                                                <div
                                                    id="bootcamp-sessions-dates"
                                                    className="d-lg-inline-block py-2 pr-2"
                                                >
                                                    <BootcampSessionDatesIcon
                                                        aria-label="view session dates"
                                                        className="text-white"
                                                    />
                                                    <span
                                                        className="pl-1"
                                                        style={{ color: '#15aeff', cursor: 'pointer' }}
                                                        onClick={() => setShowInfosecModal(true)}
                                                        onKeyDown={() => setShowInfosecModal(true)}
                                                        role="button"
                                                        tabIndex={0}
                                                    >
                                                        View Session Dates
                                                    </span>
                                                </div>
                                            )}
                                            <InfoSecModal
                                                onHide={() => setShowInfosecModal(false)}
                                                show={showInfosecModal}
                                                sessions={infosecModel.sessionDates}
                                            />
                                        </Col>
                                    </Row>
                                    {isExternshipProgram && (
                                        <Row>
                                            <Col>
                                                <div className="d-lg-inline-block py-2">
                                                    <ExternshipIcon
                                                        className="text-white"
                                                        aria-label="externship program icon"
                                                    />
                                                    <span className="pl-1">Clinical Experience</span>
                                                </div>
                                            </Col>
                                        </Row>
                                    )}
                                    <div className="border-bottom border-white d-md-none" />
                                </Col>
                            </Row>
                        </Col>
                    </Row>
                </Container>
            </Jumbotron>
        </>
    );
};

export { ProductBanner, ProductBannerProps };
